import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { BRANCH_META } from "@/lib/branches";
import { motion } from "framer-motion";
import { Target, DollarSign, Zap, Clock, ArrowRight, HandHeart, Rocket } from "lucide-react";

const STATUS_META = {
    listed: { label: "Open", color: "#3EB489", bg: "#DBF3E9" },
    claimed: { label: "In progress", color: "#FFC107", bg: "#FFF3CC" },
    submitted: { label: "Under review", color: "#1E88E5", bg: "#DCEBFC" },
    awarded: { label: "Awarded", color: "#6F00FF", bg: "#EADEFF" },
    canceled: { label: "Canceled", color: "#94A3B8", bg: "#F1F5F9" },
};

export default function BountiesBrowse() {
    const [items, setItems] = useState([]);
    const [rewardFilter, setRewardFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState("listed");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        const p = new URLSearchParams();
        if (rewardFilter) p.set("reward_type", rewardFilter);
        if (statusFilter) p.set("status", statusFilter);
        p.set("limit", "60");
        try {
            const { data } = await api.get(`/bounties/browse?${p}`);
            setItems(data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [rewardFilter, statusFilter]);

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="crystal rounded-3xl p-6 sm:p-8">
                    <p className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                        <Target className="w-3 h-3" /> Bounties
                    </p>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                        Ship one thing. Get paid.
                    </h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">
                        Real projects with real rewards — XP or cash. Claim one, ship the work,
                        collect. Cash bounties are paid off-platform by the owner (Project Atlas just
                        connects you).
                    </p>

                    <div className="mt-6 flex flex-wrap gap-1.5">
                        {[
                            { k: null, label: "All rewards" },
                            { k: "xp", label: "XP" },
                            { k: "cash", label: "Cash" },
                        ].map((f) => (
                            <button
                                key={String(f.k)}
                                onClick={() => setRewardFilter(f.k)}
                                className={`px-3 py-1.5 rounded-full text-xs font-display font-bold whitespace-nowrap transition-all ${rewardFilter === f.k ? "bg-slate-900 text-cream" : "bg-white/70 text-slate-700"}`}
                                data-testid={`bounties-reward-${f.k || "all"}`}
                            >
                                {f.label}
                            </button>
                        ))}
                        <div className="mx-2 w-px bg-slate-200" />
                        {[
                            { k: "listed", label: "Open" },
                            { k: "claimed", label: "In progress" },
                            { k: "awarded", label: "Awarded" },
                            { k: "", label: "All" },
                        ].map((f) => (
                            <button
                                key={f.k}
                                onClick={() => setStatusFilter(f.k)}
                                className={`px-3 py-1.5 rounded-full text-xs font-display font-bold whitespace-nowrap transition-all ${statusFilter === f.k ? "bg-slate-900 text-cream" : "bg-white/70 text-slate-700"}`}
                                data-testid={`bounties-status-${f.k || "all"}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {loading && (
                        <div className="col-span-2 text-sm text-slate-500 font-display">Loading bounties…</div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="col-span-2 crystal rounded-3xl p-10 text-center">
                            <Target className="w-8 h-8 mx-auto text-slate-400" />
                            <h2 className="font-display font-bold text-2xl tracking-tighter text-slate-900 mt-3">
                                No bounties in this view.
                            </h2>
                            <p className="text-slate-600 mt-1">
                                Own a project? <Link to="/projects" className="font-bold underline">Post one there.</Link>
                            </p>
                        </div>
                    )}
                    {items.map((b, i) => <BountyCard key={b.id} b={b} idx={i} />)}
                </div>
            </div>
        </div>
    );
}

function BountyCard({ b, idx }) {
    const branch = b.branch || "business";
    const meta = BRANCH_META[branch] || BRANCH_META.business;
    const status = STATUS_META[b.status] || STATUS_META.listed;
    const RewardIcon = b.reward_type === "cash" ? DollarSign : Zap;
    return (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
            <Link
                to={`/bounties/${b.id}`}
                className="crystal rounded-2xl p-5 block hover:scale-[1.01] transition-transform group"
                data-testid={`bounty-card-${b.id}`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                        {b.project_title}
                    </span>
                    <span className="chip text-[10px]" style={{ background: status.bg, color: status.color, padding: "0.15rem 0.55rem" }}>
                        {status.label}
                    </span>
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900 tracking-tight mt-2 leading-tight">
                    {b.title}
                </h3>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{b.description}</p>
                <div className="mt-3 flex items-center justify-between">
                    <div
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-display font-black text-sm"
                        style={{
                            background: b.reward_type === "cash" ? BRANCH_META.trades.soft : BRANCH_META.humanities.soft,
                            color: b.reward_type === "cash" ? BRANCH_META.trades.text : BRANCH_META.humanities.text,
                        }}
                    >
                        <RewardIcon className="w-4 h-4" />
                        {b.reward_type === "cash" ? `$${b.amount_usd}` : `${b.amount_xp} XP`}
                    </div>
                    <span className="text-xs text-slate-500">
                        Posted by <span className="font-bold text-slate-800">{b.posted_by_name}</span>
                    </span>
                </div>
            </Link>
        </motion.div>
    );
}
